import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { RecipeService } from '../core/services/recipe.service';
import { Recipe } from '../shared/models/recipe.model';
import {
  AllergenStatistics,
  CategoryStatistics,
} from '../shared/models/statistics.model';
import { withEntitiesManagement } from './features/entities-management.feature';
import { inject } from '@angular/core';
import { SnackbarService } from '../core/services/snackbar.service';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
} from '@angular-architects/ngrx-toolkit';

const statsCollection = 'recipesStats';
const imageCollection = 'recipesImage';

export interface RecipesState {
  allergensStatistics: AllergenStatistics[];
  categoriesStatistics: CategoryStatistics[];
}

const initialRecipesState: RecipesState = {
  allergensStatistics: [],
  categoriesStatistics: [],
};

export const RecipesStore = signalStore(
  { providedIn: 'root' },
  withEntitiesManagement<Recipe>(RecipeService, 'recipes', 'recipes'),
  withState<RecipesState>(initialRecipesState),
  withCallState({ collections: [statsCollection, imageCollection] }),
  withMethods(
    (
      store,
      recipeService = inject(RecipeService),
      snackbarService = inject(SnackbarService)
    ) => ({
      initialize() {
        store.loadTotalCountFromDB();
      },
      loadAllergensStatistics() {
        patchState(store, setLoading(statsCollection));

        recipeService.loadAllergensStatistics().subscribe({
          next: (entity) => {
            patchState(
              store,
              {
                allergensStatistics: entity,
              },
              setLoaded(statsCollection)
            );
          },
          error: (error) => {
            const errorMessage =
              error.error?.message || 'Failed to load allergens statistics';
            snackbarService.show(errorMessage, 'error');
            console.error('Failed to load allergens statistics', error);
            patchState(store, setError(errorMessage, statsCollection));
          },
        });
      },

      loadCategoriesStatistics() {
        patchState(store, setLoading(statsCollection));

        recipeService.loadCategoriesStatistics().subscribe({
          next: (entity) => {
            patchState(
              store,
              {
                categoriesStatistics: entity,
              },
              setLoaded(statsCollection)
            );
          },
          error: (error) => {
            const errorMessage =
              error.error?.message || 'Failed to load categories statistics';
            snackbarService.show(errorMessage, 'error');
            console.error('Failed to load categories statistics', error);
            patchState(store, setError(errorMessage, statsCollection));
          },
        });
      },

      generateImage(recipeId: string) {
        patchState(store, setLoading(imageCollection));
 
        recipeService.generateImage(recipeId).subscribe({
          next: (imageUrl) => {
            const selected = store.selectedEntity();
            if (selected) {
              patchState(
                store,
                {
                  selectedEntity: {
                    ...selected,
                    imageUrl,
                  },
                },
                setLoaded(imageCollection)
              );
            } else {
              patchState(store, setLoaded(imageCollection));
            }
            snackbarService.show('Recipe saved successfully', 'success');
          },
          error: (error) => {
            const errorMessage =
              error.error?.message || 'Failed to generate image for recipe';
            snackbarService.show(errorMessage, 'error');
            console.error('Failed to generate image for recipe', error);
            patchState(store, setError(errorMessage, imageCollection));
          },
        });
      },
 
      duplicateEntity(recipeId: string) {
        patchState(store, setLoading(imageCollection));
 
        recipeService.duplicate(recipeId).subscribe({
          next: (createdRecipe) => {
            snackbarService.show('Recipe duplicated successfully', 'success');
            // Refresh list and total count via store methods provided by feature
            store.loadTotalCountFromDB();
            store.loadEntities();
            patchState(store, setLoaded(imageCollection));
          },
          error: (error) => {
            const errorMessage =
              error.error?.message || 'Failed to duplicate recipe';
            snackbarService.show(errorMessage, 'error');
            console.error('Failed to duplicate recipe', error);
            patchState(store, setError(errorMessage, imageCollection));
          },
        });
      },
    })
  )
);
